<template>
  <!-- one inbox for the clients' meal logs (mobile API feed) and athlisis updates (core notifications) -->
  <b-nav-item-dropdown id="notif-list" ref="notif-list" class="tw-group d-none d-sm-block" right data-testid="toolbar-notifications"
                       :toggle-class="[cls.ICON_BUTTON, cls.DROPDOWN_OPEN]"
                       menu-class="custom-dropdown tw-w-[360px] tw-p-0 tw-overflow-hidden"
                       @show="opened" @hide="closed"
  >
    <template slot="button-content">
      <app-icon name="bell"/>
      <span class="tw-sr-only">{{ $t('topbar.news.title') }}</span>
      <b-badge v-if="unreadTotal" :class="cls.BADGE" class="rounded-circle" pill variant="danger" data-testid="notifications-badge">{{ unreadTotal }}</b-badge>
    </template>

    <!-- kept out of the toggle slot so badge changes don't re-create it; never shown over the open inbox -->
    <b-tooltip :target="toggleId" triggers="hover" placement="bottom" @show="e => isOpen && e.preventDefault()">
      {{ $t('topbar.news.title') }}
    </b-tooltip>

    <li v-if="shouldRender" role="presentation" class="tw-flex tw-flex-col tw-max-h-[min(540px,calc(100vh_-_90px))]">
      <div class="tw-flex tw-items-center tw-justify-between tw-px-3.5 tw-pt-3.5 tw-pb-2.5">
        <span class="tw-text-sm tw-font-semibold tw-text-gray-700">{{ $t('topbar.news.title') }}</span>
        <a v-if="unreadTotal" data-testid="notifications-mark-read"
           class="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-primary tw-cursor-pointer hover:tw-no-underline"
           @click="markAllAsRead"
        >
          <app-icon name="checks" :size="14"/>{{ $t('topbar.markAsRead') }}
        </a>
      </div>

      <div class="tw-flex tw-gap-1.5 tw-px-3 tw-pb-1.5">
        <button v-for="option in filters" :key="option.key" type="button"
                :class="filter === option.key
                  ? 'tw-bg-light-bg tw-text-primary tw-border-primary/30'
                  : 'tw-bg-white tw-text-primary-ink tw-border-slate-200 hover:tw-text-primary'"
                class="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-border tw-border-solid tw-transition-colors focus:tw-outline-none"
                @click="filter = option.key"
        >
          {{ option.label }}
          <span v-if="option.unread" class="tw-text-[10.5px] tw-font-semibold tw-text-primary">{{ option.unread }}</span>
        </button>
      </div>

      <div v-if="groups.length" class="tw-min-h-0 tw-overflow-y-auto tw-px-1.5 tw-pb-1.5">
        <template v-for="group in groups">
          <div :key="group.label" class="tw-px-2 tw-pt-2 tw-pb-1 tw-text-[11px] tw-font-semibold tw-text-primary-muted">{{ group.label }}</div>
          <a v-for="item in group.items" :key="item.key" data-testid="notifications-item"
             :class="{ 'tw-bg-light-bg': item.unread }"
             class="tw-flex tw-items-center tw-gap-2.5 tw-p-2 tw-rounded-[10px] tw-cursor-pointer tw-transition-colors hover:tw-bg-light-bg hover:tw-no-underline"
             @click="openItem(item)"
          >
            <person-avatar v-if="item.kind === 'meal'" :person="item.client" size="sm"/>
            <span v-else
                  class="tw-flex tw-items-center tw-justify-center tw-w-[30px] tw-h-[30px] tw-flex-shrink-0 tw-rounded-[10px] tw-bg-light-bg tw-border tw-border-solid tw-border-slate-200 tw-text-primary-ink"
            >
              <app-icon :name="item.icon" :size="16"/>
            </span>
            <span class="tw-flex-1 tw-min-w-0">
              <span class="tw-block tw-truncate tw-text-[12.5px] tw-leading-snug tw-text-gray-700">
                <template v-if="item.kind === 'meal'">
                  {{ $t('topbar.news.newMealLog') }} <b class="tw-font-semibold tw-text-primary">{{ item.client.firstName }} {{ item.client.lastName }}</b>
                </template>
                <template v-else>{{ item.title }}</template>
              </span>
              <span class="tw-block tw-mt-0.5 tw-text-[11.5px] tw-text-primary-muted">{{ item.meta }}</span>
            </span>
            <span v-if="item.unread" class="tw-w-[7px] tw-h-[7px] tw-flex-shrink-0 tw-rounded-full tw-bg-primary"/>
          </a>
        </template>
      </div>

      <empty-state v-else-if="feedReady || filter === 'updates'" compact
                   :icon="emptyState.icon" :title="emptyState.title" :subtitle="emptyState.text"
      />
      <div v-else class="tw-flex tw-justify-center tw-py-6">
        <b-spinner small variant="primary"/>
      </div>

      <div class="tw-px-3.5 tw-py-2.5 tw-border-0 tw-border-t tw-border-solid tw-border-slate-100">
        <a class="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-primary-ink tw-cursor-pointer hover:tw-text-primary hover:tw-no-underline"
           @click="openSettings"
        >
          <app-icon name="settings" :size="14"/>{{ $t('topbar.news.manageNotifications') }}
        </a>
      </div>
    </li>
  </b-nav-item-dropdown>
</template>
<script>
import axios from "axios";
import moment from "moment";
import {mapState} from "vuex";
import localforage from "localforage";
import {
  MOBILE_FEED_DATA,
  MOBILE_FEED_LAST_FETCH,
  MOBILE_FEED_LATEST_STAMP,
  SHOW_MOBILE_NOTIFICATION_FEED
} from "@utils/storageKeys";
import {appMealTypes} from "@utils/constants";
import AppIcon from "@components/icons/AppIcon.vue";
import PersonAvatar from "@components/global/PersonAvatar.vue";
import * as toolbarClasses from "@components/top-bar/toolbarClasses";
import {blurToggleAfterPointerClose} from "@utils/dropdownFocus";
import {pollWhileVisible} from "@utils/pollWhileVisible";
import {refreshNotifications, useNotifications} from "@src/queries/notifications";

// the inbox shows the newest items; the full history lives on the notifications page
const MAX_ITEMS = 30;

export default {
  components: {AppIcon, PersonAvatar},

  setup() {
    return useNotifications();
  },

  data() {
    return {
      cls: Object.freeze({...toolbarClasses}),
      // bootstrap-vue renders the dropdown toggle with this id
      toggleId: 'notif-list__BV_toggle_',
      feed: [],
      feedLoaded: false,
      users: {},
      mealNotifCount: 0,
      stopPoll: null,
      shouldRender: false,
      isOpen: false,
      disabled: false,
      filter: 'all',
    }
  },

  computed: {
    ...mapState({
      mobileFeedNotifications: state => state.settings.userSettings ? state.settings.userSettings.mobile_feed_notifications : true,
      mobileToken: state => state.auth.mobileToken,
    }),
    // without a mobile token there is no feed to wait for
    feedReady() {
      return this.feedLoaded || !this.mobileToken;
    },
    emptyState() {
      return {
        all: {icon: 'fe-inbox', title: this.$t('topbar.news.noItems'), text: this.$t('topbar.news.noItemsText')},
        meals: {icon: 'fe-smartphone', title: this.$t('topbar.news.noMeals'), text: this.$t('topbar.news.noMealsText')},
        updates: {icon: 'fe-bell', title: this.$t('topbar.news.noNews'), text: this.$t('topbar.news.noNewsText')},
      }[this.filter];
    },
    unreadTotal() {
      return (this.notificationCount || 0) + this.mealNotifCount;
    },
    filters() {
      return [
        {key: 'all', label: this.$t('topbar.news.filterAll')},
        {key: 'meals', label: this.$t('topbar.news.filterMeals'), unread: this.mealNotifCount},
        {key: 'updates', label: this.$t('topbar.news.filterUpdates'), unread: this.notificationCount},
      ];
    },
    // the feed is newest-first and its first `mealNotifCount` items arrived since the inbox was last opened
    mealItems() {
      return this.feed.map((item, index) => {
        const at = moment(item.created_at);
        return {
          key: `meal-${item.id}`,
          kind: 'meal',
          source: item,
          client: this.users[item.userId] || {},
          at,
          unread: index < this.mealNotifCount,
          meta: `${appMealTypes[item.type - 1]} · ${at.fromNow()}`,
        };
      });
    },
    updateItems() {
      return (this.notifications || []).map(nf => {
        // not updatedAt: the core list sends it pre-formatted as DD/MM/YYYY
        const at = moment(nf.createdAt);
        return {
          key: `update-${nf.id}`,
          kind: 'update',
          source: nf,
          title: nf.title,
          // client-file notifications link to the client; the rest are athlisis news
          icon: nf.url && nf.url.startsWith('/athletes') ? 'file-text' : 'speakerphone',
          at,
          unread: !nf.isRead,
          meta: `${this.$t('topbar.news.update')} · ${at.fromNow()}`,
        };
      });
    },
    groups() {
      const items = {
        all: [...this.mealItems, ...this.updateItems],
        meals: this.mealItems,
        updates: this.updateItems,
      }[this.filter];

      const groups = [];
      [...items]
        .sort((a, b) => b.at - a.at)
        .slice(0, MAX_ITEMS)
        .forEach(item => {
          const label = this.dayLabel(item.at);
          const last = groups[groups.length - 1];
          if (last && last.label === label) last.items.push(item);
          else groups.push({label, items: [item]});
        });
      return groups;
    },
  },

  created() {
    if (this.mobileToken) this.startPolling();
  },

  mounted() {
    blurToggleAfterPointerClose(this.$refs['notif-list']);
  },

  watch: {
    mobileToken(val) {
      if (val) this.startPolling();
    },
    mobileFeedNotifications(enabled) {
      if (!enabled) this.mealNotifCount = 0;
    },
  },

  methods: {
    dayLabel(day) {
      if (day.isSame(moment(), 'day')) return this.$t('topbar.news.today');
      if (day.isSame(moment().subtract(1, 'day'), 'day')) return this.$t('topbar.news.yesterday');
      return day.format(day.isSame(moment(), 'year') ? 'D MMMM' : 'D MMMM YYYY');
    },

    go(route) {
      this.$refs['notif-list'].hide();
      this.$router.push(route);
    },

    openSettings() {
      this.$refs['notif-list'].hide();
      this.$store.dispatch('settings/openSettingsModal');
    },

    openItem(item) {
      if (item.kind === 'meal') return this.openMealLog(item.source);
      // client-file notifications carry the athlete's files page
      this.go(item.source.url || {name: 'user-notifications'});
    },

    startPolling() {
      if (this.stopPoll) return;
      this.getMobileFeedData();
      this.stopPoll = pollWhileVisible(() => this.getMobileFeedData(), 10 * 1000);
    },

    stopPolling() {
      if (!this.stopPoll) return;
      this.stopPoll();
      this.stopPoll = null;
    },

    async getMobileFeedData() {
      const lastFetch = await localforage.getItem(MOBILE_FEED_LAST_FETCH);
      const currentData = await localforage.getItem(MOBILE_FEED_DATA);
      const notificationsCount = await localforage.getItem(SHOW_MOBILE_NOTIFICATION_FEED);

      // a sync within the last 2 minutes is fresh enough
      if (lastFetch && new Date(lastFetch) > new Date(Date.now() - 2 * 60 * 1000)) {
        this.feed = currentData?.feed || [];
        this.users = currentData?.users || {};
        this.mealNotifCount = this.mobileFeedNotifications ? (notificationsCount || 0) : 0;
        this.feedLoaded = true;
        return;
      }

      try {
        const cursor = await localforage.getItem(MOBILE_FEED_LATEST_STAMP);
        const {data} = await axios.get(process.env.VUE_APP_MOBILE_API + "/dietitians/feeds/meals", {
          params: cursor ? {cursor} : {}
        });

        const incomingFeed = data.feed || [];
        const cachedFeed = currentData?.feed || [];
        const cachedUsers = currentData?.users || {};

        // Merge incoming with cached, dedupe by id, and keep the latest 50 items
        const seenIds = new Set();
        const mergedFeed = [...incomingFeed, ...cachedFeed]
          .filter(item => {
            if (seenIds.has(item.id)) return false;
            seenIds.add(item.id);
            return true;
          })
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 50);

        const mergedUsers = {...cachedUsers, ...(data.users || {})};

        await localforage.setItem(MOBILE_FEED_DATA, {feed: mergedFeed, users: mergedUsers});
        await localforage.setItem(MOBILE_FEED_LAST_FETCH, new Date().toISOString());
        if (mergedFeed.length > 0) {
          await localforage.setItem(MOBILE_FEED_LATEST_STAMP, mergedFeed[0].created_at);
        }

        this.feed = mergedFeed;
        this.users = mergedUsers;

        if (cachedFeed.length > 0) {
          const cachedIds = new Set(cachedFeed.map(c => c.id));
          const newItemsCount = (notificationsCount || 0) + incomingFeed.filter(item => !cachedIds.has(item.id)).length;
          await localforage.setItem(SHOW_MOBILE_NOTIFICATION_FEED, newItemsCount);
          this.mealNotifCount = this.mobileFeedNotifications ? newItemsCount : 0;
        }
      } catch (e) {
        console.log(e);
      }
      this.feedLoaded = true;
    },

    async resetMealCount() {
      await localforage.setItem(SHOW_MOBILE_NOTIFICATION_FEED, 0);
      this.mealNotifCount = 0;
    },

    markAllAsRead() {
      this.resetMealCount();
      if (!this.notificationCount) return;
      this.$axios.put('/notifications/markAllAsRead')
        .then(() => refreshNotifications())
        .catch(() => this.emitErrorNotification({duration: 5000}));
    },

    closed() {
      this.isOpen = false;
      this.resetMealCount();
    },

    opened() {
      this.shouldRender = true;
      this.isOpen = true;
      this.$root.$emit('bv::hide::tooltip', this.toggleId);
    },

    async openMealLog(item) {
      if (this.disabled) return;
      this.disabled = true;
      try {
        const {data} = await axios.get(`${process.env.VUE_APP_API}/athlete`, {
          params: {mobileClientId: item.userId}
        });
        if (data.count === 0) throw new Error(`No user found with id ${item.userId}`);
        this.go(`/athletes/${data.rows[0].id}#mobile-app`);
      } catch (e) {
        this.emitErrorNotification({duration: 5000, text: e});
      } finally {
        this.disabled = false;
      }
    },
  },

  destroyed() {
    this.stopPolling();
  }
}
</script>
